import * as Yup from 'yup';
import { Base64File } from '@synergy/models';
import { Box, FormHelperText, Grid, Stack } from '@mui/material';
import { ErrorCode, FileRejection } from 'react-dropzone';
import { Field, FieldProps, Formik, FormikProps, isNaN } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    FormikPoolDatePicker,
    PoolMarkdownEditor,
    PoolSpinner,
    PoolTextField,
    PoolTypography,
    ScrollToFieldError,
} from '@synergy/components';
import { ImageMimeType, ImageRatio, ImageType } from '@synergy/models';
import { MAX_FILE_SIZE } from '@synergy/config';
import { PaloFormSection } from '@synergy/components-palo';
import { PoolCheckbox, PoolDocumentTitle } from '@synergy/components-next';
import { base64encode, fields } from '@synergy/utils';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import * as SponsorBannerActions from '../store/sponsor-banner-actions';
import * as SponsorBannerDetailActions from './store/sponsor-banner-detail-actions';
import { PaloContentDetailShell } from '../../palo/palo-content-detail-shell';
import {
    PartialSupplier,
    PoolSupplierAutocompleteList,
} from '../../../../common/components/autocomplete-list/supplier-autocomplete-list/pool-supplier-autocomplete-list';
import { PictureDropzone } from '../../components/picture-dropzone';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { SponsorBannerFormValues } from './models/sponsor-banner-form-values';
import {
    sponsorBannerDetailFormValues,
    sponsorBannerDetailRequestingSelector,
    sponsorBannerDetailSelector,
} from './store/sponsor-banner-detail-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const validImageTypes = [ImageType.JPEG, ImageType.PNG, ImageType.WebP, ImageType.GIF];
const validImageMimeTypes = validImageTypes.map(type => ImageMimeType[type].toString());

export const SponsorBannerDetail = () => {
    const requesting = useAppSelector(sponsorBannerDetailRequestingSelector);
    const sponsorBanner = useAppSelector(sponsorBannerDetailSelector);
    const sponsorBannerFormValues = useAppSelector(sponsorBannerDetailFormValues);
    const dispatch = useThunkDispatch();
    const { id: sponsorBannerIdParam } = useParams<{ id: string }>();
    const [newImageFile, setNewImageFile] = useState<Base64File | undefined>(undefined);
    const intl = useIntl();
    const navigate = useNavigate();
    const fieldNames = fields<SponsorBannerFormValues>();

    // only fetch initially if existing sponsorBanner
    useEffect(() => {
        if (sponsorBannerIdParam && !isNaN(+sponsorBannerIdParam)) {
            dispatch(SponsorBannerDetailActions.fetchSponsorBannerDetail(+sponsorBannerIdParam));
        }

        return () => {
            dispatch(SponsorBannerDetailActions.resetSponsorBannerDetail());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isNaN(Number(sponsorBannerIdParam)) && sponsorBanner?.id) {
            navigate(ROUTES.contentManagement.sponsorBanner.detail.replace(':id', sponsorBanner.id.toString()));
        }
        // ignore sponsorBannerIdParam, should only rerun if sponsorBanner.id changes when new sponsorBanner is created
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, sponsorBanner?.id]);

    const handleSubmit = (values: SponsorBannerFormValues) => {
        dispatch(SponsorBannerDetailActions.saveSponsorBannerDetail(values));
    };

    const handleDelete = () => {
        if (sponsorBannerIdParam && !isNaN(sponsorBannerIdParam)) {
            dispatch(SponsorBannerActions.deleteSponsorBanner(+sponsorBannerIdParam));
        }
    };

    const isNew = isNaN(Number(sponsorBannerIdParam));

    const handleDrop =
        (form: FormikProps<SponsorBannerFormValues>, name: string) =>
        async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
            if (rejectedFiles.length > 0) {
                if (rejectedFiles[0].errors[0].code === ErrorCode.FileInvalidType) {
                    form.setFieldError(name, 'app.error.invalidImageType');
                } else if (rejectedFiles[0].errors[0].code === ErrorCode.FileTooLarge) {
                    form.setFieldError(name, 'app.error.maxFiveMb');
                } else {
                    form.setFieldError(name, 'app.error.maxOneFile');
                }

                form.setFieldTouched(name, true, false);

                return;
            }

            if (!acceptedFiles[0]?.type || !validImageMimeTypes.includes(acceptedFiles[0].type)) {
                form.setFieldError(name, 'app.error.invalidImageType');
                form.setFieldTouched(name, true, false);
                return;
            }

            const image = { name: acceptedFiles[0].name, contentBase64: await base64encode(acceptedFiles[0]) };

            if (name === 'image') {
                setNewImageFile(image);
            }

            form.setFieldValue(name, image);
        };

    const validationSchema = Yup.object().shape({
        image: Yup.mixed().required('app.error.required'),
        url: Yup.string(),
        validFrom: Yup.date().required('app.error.required').typeError('app.error.required'),
        validTo: Yup.date()
            .required('app.error.required')
            .typeError('app.error.required')
            .min(Yup.ref('validFrom'), 'app.error.afterValidFrom'),
        supplierId: Yup.mixed(),
        hint: Yup.string(),
    });

    return (
        <PoolSpinner enabled={requesting} sx={{ width: '100%', height: '100%' }}>
            <PoolDocumentTitle
                titleTranslationId={'app.contentManagement.sponsorBanner'}
                name={sponsorBanner?.supplier?.name}
            />
            <Formik
                initialValues={sponsorBannerFormValues}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
                enableReinitialize={true}
            >
                <PaloContentDetailShell
                    title="app.contentManagement.sponsorBanner.detailHeader"
                    isNew={isNew}
                    onDelete={handleDelete}
                    listRoute={ROUTES.contentManagement.sponsorBanner.list}
                    deleteTitle={<FormattedMessage id="app.contentManagement.sponsorBanner.delete" />}
                    deleteWarning={<FormattedMessage id="app.contentManagement.sponsorBanner.deleteWarning" />}
                    translatable={false}
                >
                    <ScrollToFieldError />

                    <Box m={theme => theme.spacing(2, theme.general.pageContentSpacing)}>
                        <PaloFormSection title={<FormattedMessage id="app.contentManagement.sponsorBanner.supplier" />}>
                            <Grid item={true} xs={12}>
                                <Field name={fieldNames.supplier}>
                                    {({ field, form, meta }: FieldProps<PartialSupplier>) => (
                                        <>
                                            <PoolSupplierAutocompleteList
                                                label={<FormattedMessage id="app.freePosition.supplierAccount" />}
                                                onSelected={supplier => form.setFieldValue(field.name, supplier)}
                                                initialValue={field.value?.name}
                                                error={meta.touched && !!meta.error}
                                                disablePreventOverflow={true}
                                                isLocked={false}
                                                {...field}
                                            />
                                            {meta.touched && meta.error && (
                                                <FormHelperText error={true}>
                                                    <FormattedMessage id={meta.error} />
                                                </FormHelperText>
                                            )}
                                        </>
                                    )}
                                </Field>
                            </Grid>
                        </PaloFormSection>

                        <PaloFormSection title={<FormattedMessage id="app.label.internalComment" />} collapsible={true}>
                            <Grid item={true} xs={12}>
                                <Field name={fieldNames.note}>
                                    {({ field, meta, form }: FieldProps<string>) => (
                                        <>
                                            <PoolMarkdownEditor
                                                {...field}
                                                value={field.value}
                                                onChange={value => form.setFieldValue(field.name, value)}
                                                error={!!meta.touched && !!meta.error}
                                            />
                                            {meta.touched && meta.error ? (
                                                <FormHelperText error={true} sx={{ ml: 1.75 }}>
                                                    <FormattedMessage id={meta.error} />
                                                </FormHelperText>
                                            ) : null}
                                        </>
                                    )}
                                </Field>
                            </Grid>
                        </PaloFormSection>

                        <PaloFormSection title={<FormattedMessage id="app.contentManagement.picture" />}>
                            <Grid container={true} spacing={2}>
                                <Grid item={true} xs={6}>
                                    <Field name={fieldNames.image}>
                                        {({ field, form, meta }: FieldProps<Base64File>) => (
                                            <>
                                                <PictureDropzone
                                                    onDrop={handleDrop(form, field.name)}
                                                    error={!!meta.error && !!meta.touched}
                                                    maxSize={MAX_FILE_SIZE}
                                                    path={newImageFile?.contentBase64 ?? field.value?.pathname}
                                                    imageRatio={ImageRatio.TwoByOne}
                                                    alt={field.value?.name}
                                                >
                                                    <PoolTypography
                                                        typography="body2"
                                                        component="span"
                                                        color="grey.700"
                                                        id="app.contentManagement.picture.desktopMobile"
                                                        values={{ validImageTypes: validImageTypes.join(', ') }}
                                                    />
                                                    <PoolTypography
                                                        typography="body2"
                                                        component="span"
                                                        color="grey.700"
                                                        id="app.contentManagement.picture.constraint"
                                                        values={{ resolution: '740x370px (2:1)' }}
                                                    />
                                                </PictureDropzone>
                                                {!!meta.error && !!meta.touched && (
                                                    <FormHelperText error={true} sx={{ ml: 1.75 }}>
                                                        <FormattedMessage
                                                            id={meta.error}
                                                            values={{
                                                                validImageTypes: validImageTypes.join(', '),
                                                            }}
                                                        />
                                                    </FormHelperText>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </Grid>
                                <Grid item={true} xs={12}>
                                    <Field name={fieldNames.isAnimated}>
                                        {({ field, meta, form }: FieldProps<boolean>) => (
                                            <>
                                                <Stack
                                                    gap={1}
                                                    direction="row"
                                                    sx={{ mb: 2, cursor: 'pointer', alignItems: 'center' }}
                                                    onClick={() => form.setFieldValue(field.name, !field.value)}
                                                >
                                                    <PoolCheckbox
                                                        variant="round"
                                                        name={field.name}
                                                        onBlur={field.onBlur}
                                                        checked={field.value}
                                                    />
                                                    <FormattedMessage id="app.contentManagement.sponsorBanner.animated" />
                                                </Stack>
                                                {!!meta.error && !!meta.touched && (
                                                    <FormHelperText error={true} sx={{ ml: 1.75 }}>
                                                        <FormattedMessage id={meta.error} />
                                                    </FormHelperText>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </Grid>
                                <Grid item={true} xs={12}>
                                    <Field name={fieldNames.url}>
                                        {({ field, meta }: FieldProps<string>) => (
                                            <PoolTextField
                                                {...field}
                                                fullWidth={true}
                                                label={
                                                    <FormattedMessage id="app.contentManagement.sponsorBanner.url" />
                                                }
                                                placeholder={intl.formatMessage({
                                                    id: 'app.contentManagement.sponsorBanner.urlPlaceholder',
                                                })}
                                                error={!!meta.touched && !!meta.error}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} />
                                                    ) : null
                                                }
                                            />
                                        )}
                                    </Field>
                                </Grid>
                            </Grid>
                        </PaloFormSection>

                        <PaloFormSection
                            title={<FormattedMessage id="app.contentManagement.sponsorBanner.displayTime" />}
                        >
                            <Grid container={true} spacing={2}>
                                <Grid item={true} xs={6}>
                                    <FormikPoolDatePicker
                                        name={fieldNames.validFrom}
                                        disablePast={true}
                                        label={
                                            <FormattedMessage id="app.contentManagement.sponsorBanner.displayTimeValidFrom" />
                                        }
                                        required={true}
                                    />
                                </Grid>
                                <Grid item={true} xs={6}>
                                    <FormikPoolDatePicker
                                        name={fieldNames.validTo}
                                        disablePast={true}
                                        label={
                                            <FormattedMessage id="app.contentManagement.sponsorBanner.displayTimeValidTo" />
                                        }
                                        required={true}
                                    />
                                </Grid>
                            </Grid>
                        </PaloFormSection>
                    </Box>
                </PaloContentDetailShell>
            </Formik>
        </PoolSpinner>
    );
};
